/**
 * App-Shell — Rail + Sub-Sidebar + Topbar + Main-Container (Phase 3.5 §5).
 *
 * 1:1 zu `cotrainer-apps-script/app/Index.html`. Shell wird einmal gemountet
 * (von main.ts nach probeAuth), bleibt persistent zwischen Routenwechseln.
 *
 * K1-Update (2026-05-24): Alle 9 Apps-Script-Bereiche werden in der Rail
 * gerendert. Coming-Soon-Bereiche bekommen Lock-Overlay + disabled-State.
 * K2-Update (2026-05-24): Breadcrumb für Hub-Bereich zeigt nur "Hub",
 * für andere Bereiche "Bereich › View".
 */

import { logoutBackend, vertretungCount } from "../api/client";
import { widerrufZaehler, type WiderrufFall } from "../api/widerruf";
// 🔑 Regeln, kein Aussehen: `w-rahmen.ts` importiert seit dem 20.09. keine
// Stylesheets mehr, damit die Leiste `wartetAuf` benutzen kann, ohne das
// Widerruf-CSS auf jede Seite der App zu ziehen.
import { rolleAus, wartetAuf } from "../views/widerruf/w-rahmen";
// 🔑 Dieselbe Regel wie dort, aus demselben Grund: die Leiste rechnet mit der
// Funktion der Liste, nicht mit einer Nachbildung. `k-rahmen.ts` zieht ebenfalls
// kein Stylesheet — sonst hinge das Kündigungs-CSS an jeder Seite der App.
import { kRolleAus, wartetAufZeile } from "../views/kuendigung/k-rahmen";
import { kuendigungUebersicht } from "../api/kuendigung";
import type { AppBootstrapResult, HubContext, UserContext } from "../api/types";
import {
  type BereichDef,
  type GroupDef,
  type ViewDef,
  allBereicheForRail,
  defaultRouteForGroup,
  einstiegFuerUser,
  findGroupForView,
  findViewForRoute,
  flattenedViews,
  isBereichLiveForUser,
  visibleGroupsForUser,
  visibleViewsForUser
} from "../lib/bereiche";
import { icon } from "../lib/icons";
import { avatarHtml, hydrateAvatars } from "./avatar";
import { mountTopbarStempel } from "../views/_topbar-stempel";
import { escapeHtml, escapeAttr } from "../lib/html";

const SHELL_ID = "app-shell";
const VIEW_CONTAINER_ID = "view-container";

let currentUser: UserContext | null = null;
let subsidebarCollapsed = false;
/**
 * Vom Nutzer aufgeklappte Oberkategorien (Sitzungszustand, Justin 2026-08-14).
 * Die AKTIVE Gruppe ist immer offen und steht deshalb NICHT hier drin — sonst
 * bliebe sie beim Weiterklicken fälschlich offen.
 */
const offeneGruppen = new Set<string>();
/**
 * Sub-Iter 2: Bootstrap-Response wird vom Hub-View nach erfolgreicher Last
 * via setBootstrap() global gecached. Topbar liest daraus die Pills (kein
 * 2. /app/bootstrap-Call). Wenn null → Topbar zeigt keine Pills.
 */
let cachedBootstrap: AppBootstrapResult | null = null;

export function setBootstrap(b: AppBootstrapResult | null): void {
  cachedBootstrap = b;
  // Topbar neu rendern wenn schon gemounted (Pills füllen sich nach Hub-Load)
  const tb = document.getElementById("topbar");
  if (tb && currentUser) {
    tb.innerHTML = topbarHtml(currentUser);
    attachTopbarHandlers();
  }
}

export function getBootstrap(): AppBootstrapResult | null {
  return cachedBootstrap;
}

// =============================================================================
// Mount + Update
// =============================================================================

export function mountShell(host: HTMLElement, user: UserContext): void {
  currentUser = user;
  host.innerHTML = shellHtml(user);
  attachShellHandlers();
  void hydrateAvatars(host); // Rail-Avatar async nachladen (DSGVO: fetch+Blob)
}

export function isShellMounted(): boolean {
  return document.getElementById(SHELL_ID) !== null;
}

export function getViewContainer(): HTMLElement {
  const el = document.getElementById(VIEW_CONTAINER_ID);
  if (!el) {
    throw new Error("[app-shell] view-container not mounted — call mountShell() first");
  }
  return el;
}

export function setActiveRoute(path: string): void {
  if (!currentUser) return;
  const match = findViewForRoute(path);
  const activeBereichSlug = match?.bereich.slug ?? null;
  const activeViewKey = match?.view.key ?? null;
  // 🔑 Der versteckte Reiter sagt selbst, welcher sichtbare Eintrag fuer ihn
  // leuchten soll (ViewDef.reiterVon). Ohne das hob die Leiste den Eintrag
  // hervor, auf dem man gerade NICHT ist — gemeldet 22.09.2026.
  const activeParentKey = match?.view.reiterVon ?? null;

  document.querySelectorAll<HTMLElement>("[data-rail-bereich]").forEach((el) => {
    const slug = el.dataset.railBereich;
    el.classList.toggle("is-current", slug === activeBereichSlug);
  });

  const sub = document.getElementById("subsidebar");
  const expandBtn = document.getElementById("subsidebar-expand-btn");
  if (sub) {
    const bereich = match?.bereich ?? null;
    // Hub-Bereich: Sub-Sidebar komplett verstecken (1:1 zu Apps-Script
    // Index.html Z. 985-991). Hub-View nutzt volle Breite.
    const hideSubsidebar = !bereich || bereich.slug === "hub";
    sub.classList.toggle("is-hidden", hideSubsidebar);
    if (hideSubsidebar) {
      sub.innerHTML = "";
      sub.classList.remove("is-collapsed");
      expandBtn?.classList.remove("is-visible");
    } else {
      sub.innerHTML = subsidebarHtml(currentUser, bereich, activeViewKey, activeParentKey);
      attachSubsidebarHandlers();
      void hydrateAvatars(sub); // Footer-Avatar async nachladen
      void zeigeVertretungSignal(sub);
      void zeigeWiderrufSignal(sub);
      void zeigeKuendigungSignal(sub);
      void zeigeErgebnisSignal(sub);
      void zeigeVertragsanpassungSignal(sub);
      void zeigeHrAbgleichSignal(sub);
      void zeigeSepaPapierSignal(sub);
    }
  }
}

// =============================================================================
// Signal „Vertretung klären" (E11) — betrifft es mich?
// =============================================================================

/**
 * Zähler am Nav-Eintrag „Vertretung". **Er erscheint nur, wenn es die
 * angemeldete Person angeht** — ein Host sieht die Zahl seiner eigenen
 * betroffenen Termine, die Bereichsleitung die Zahl aller.
 *
 * 🔑 Warum überhaupt ein Signal: die Klärungsliste ist eine BRINGSCHULD des
 * Systems. Wer nicht zufällig auf die Seite klickt, erfährt sonst nie, dass
 * einer seiner Termine in seinem Urlaub liegt — genau das war der Zustand
 * vorher, als die 19 Fälle nur in einem `console.warn` des Crons standen.
 *
 * Der Wert wird je Sitzung höchstens alle 5 Minuten geholt und zwischen
 * Routenwechseln wiederverwendet: die Sub-Sidebar rendert bei JEDEM Wechsel neu,
 * ein Fetch pro Klick wäre reine Last ohne neue Auskunft.
 */
const VERTRETUNG_ROUTE = "/meetings/vertretung";
const VERTRETUNG_TTL_MS = 5 * 60 * 1000;
let vertretungStand: { offen: number; istBl: boolean; geholt: number } | null = null;
let vertretungLaeuft = false;

/** Nach einem Apply aufrufen — die Zahl ist dann sofort veraltet. */
export function invalidateVertretungSignal(): void {
  vertretungStand = null;
}

async function zeigeVertretungSignal(sub: HTMLElement): Promise<void> {
  const link = sub.querySelector<HTMLAnchorElement>(`a[href="${VERTRETUNG_ROUTE}"]`);
  if (!link) return; // Nav-Eintrag nicht sichtbar → nichts zu tun

  const frisch = vertretungStand && Date.now() - vertretungStand.geholt < VERTRETUNG_TTL_MS;
  if (frisch) { malVertretungBadge(link, vertretungStand!); return; }
  if (vertretungLaeuft) return;

  vertretungLaeuft = true;
  try {
    const r = await vertretungCount();
    vertretungStand = { offen: r.offen, istBl: r.istBl, geholt: Date.now() };
    // Die Sidebar kann inzwischen neu gerendert worden sein — Element frisch suchen.
    const aktuell = document.querySelector<HTMLAnchorElement>(`#subsidebar a[href="${VERTRETUNG_ROUTE}"]`);
    if (aktuell) malVertretungBadge(aktuell, vertretungStand);
  } catch {
    // Still: ein fehlgeschlagener Zähler darf die Navigation nicht stören. Er
    // zeigt dann KEINE Zahl — nie eine 0, denn „0" wäre eine Behauptung, die
    // wir gerade nicht belegen können.
  } finally {
    vertretungLaeuft = false;
  }
}

function malVertretungBadge(link: HTMLAnchorElement, stand: { offen: number; istBl: boolean }): void {
  link.querySelector(".sub-item-count")?.remove();
  if (stand.offen <= 0) return;
  const span = document.createElement("span");
  span.className = "sub-item-count";
  span.textContent = String(stand.offen);
  span.title = stand.istBl
    ? `${stand.offen} Termine brauchen eine Vertretung`
    : `${stand.offen} deiner Termine brauchen eine Vertretung — zuweisen kann sie die Bereichsleitung`;
  link.appendChild(span);
}

// =============================================================================
// Signal „offene Widerrufe" (Justin, 14.09.2026)
// =============================================================================

/**
 * Zähler am Nav-Eintrag „Widerrufe" — in **allen drei** Bereichen.
 *
 * > Justin, 14.09.: *„bei offenen widerrufen muss claudius als BL die sidebar
 * > aufgeklappt bekommen mit einer grünen zahl wie bei vertretung mit der
 * > anzahl der noch offenen widerrufe — bei finance und kb das gleiche"* ·
 * > *„aber nur das jeweilige aufklappen was widerruf als punkt beinhaltet, also
 * > nicht die ganze sidebar — plus evtl. highlighten"*
 *
 * ═══ 🔑 WARUM ES DIESES SIGNAL BRAUCHT — gemessen, nicht vermutet ══════════
 * 📏 Am 14.09.2026 standen **vier** Vorgänge offen, dazu **sechs**
 * HubSpot-Aufgaben, **fünf davon überfällig**; die Vertriebsfrist von
 * `W-2026-0009` riss am 11.09., ohne dass es auffiel.
 * 🔑 ***Eine Liste, die man aufsuchen muss, ist eine Holschuld*** — und der
 * Widerruf ist der Vorgang, bei dem das am teuersten ist: er hat eine Frist,
 * eine Rechnungsfolge und drei Zuständige, von denen jeder denkt, ein anderer
 * sehe hin. Dieselbe Begründung wie beim Vertretungs-Signal, und sie kam dort
 * aus demselben Schaden.
 *
 * ⚠️ **Eine Zahl für alle drei Bereiche, nicht drei verschiedene.** Der Zähler
 * nennt die offenen VORGÄNGE, nicht „was auf dich wartet" — wer dran ist, steht
 * im Schirm dahinter. *Drei verschiedene Zahlen am selben Wort sind schlimmer
 * als eine gröbere, über die sich alle einig sind.*
 *
 * ⚠️ **Der Wert wird je Sitzung höchstens alle 5 Minuten geholt**, wie beim
 * Vertretungs-Signal: die Sub-Sidebar rendert bei JEDEM Routenwechsel neu.
 */
const WIDERRUF_ROUTEN = ["/widerrufe", "/kb/widerrufe", "/finance/widerrufe"] as const;
const WIDERRUF_TTL_MS = 5 * 60 * 1000;
let widerrufStand: { offen: number; meine: number | null; geholt: number } | null = null;
let widerrufLaeuft = false;
/** Gruppen, die das Signal von selbst aufgeklappt hat — je Sitzung höchstens einmal. */
const autoGeoeffnet = new Set<string>();

/** Nach einem Vorgang aufrufen — die Zahl ist dann sofort veraltet. */
export function invalidateWiderrufSignal(): void {
  widerrufStand = null;
}

/** Der Widerruf-Eintrag der AKTUELLEN Sub-Sidebar — je Bereich ein anderer Pfad. */
function widerrufLink(wurzel: ParentNode): HTMLAnchorElement | null {
  for (const r of WIDERRUF_ROUTEN) {
    const a = wurzel.querySelector<HTMLAnchorElement>(`a[href="${r}"]`);
    if (a) return a;
  }
  return null;
}

async function zeigeWiderrufSignal(sub: HTMLElement): Promise<void> {
  if (!widerrufLink(sub)) return; // Nav-Eintrag nicht sichtbar → nichts zu tun

  const frisch = widerrufStand && Date.now() - widerrufStand.geholt < WIDERRUF_TTL_MS;
  if (frisch) {
    malWiderrufBadge(widerrufStand!.meine ?? widerrufStand!.offen, widerrufStand!.meine !== null);
    return;
  }
  if (widerrufLaeuft) return;

  widerrufLaeuft = true;
  try {
    const r = await widerrufZaehler();
    // 🔴 `null` heisst **nicht messbar**, nicht „null offen". Dann bleibt der
    // Zähler weg und der Stand ungesetzt, damit der nächste Wechsel es erneut
    // versucht. *Eine 0 wäre eine Behauptung, die wir gerade nicht belegen.*
    if (typeof r.offen !== "number") return;
    // 🔴 20.09.2026 — DIE ZAHL IST „AUF DICH WARTET N" (Entscheid Justin, J5).
    //
    // Gerechnet wird mit `wartetAuf` — **derselben Funktion wie die
    // Vorgangsliste**, nicht mit einer Nachbildung. 🔑 *Zwei Orte, die dieselbe
    // Frage verschieden beantworten, sind zwei Wahrheiten, und die Leiste
    // verliert gegen die Liste, ohne dass es jemand merkt.*
    //
    // ⚠️ Die Rolle kommt aus der ADRESSE des gefundenen Eintrags, nicht aus
    // dem Nutzer allein: dieselbe Person zählt unter `/kb/widerrufe` anders
    // als unter `/widerrufe`. *Wer den Fall ansieht, ist keine Eigenschaft
    // des Falls.*
    //
    // 📌 `null` heisst **nicht gerechnet** (der Server lieferte keine Fälle) —
    // dann fällt die Anzeige auf die Gesamtzahl zurück, statt eine 0 zu
    // behaupten.
    const meine = berechneMeine(r.faelle, r.heute);
    widerrufStand = { offen: r.offen, meine, geholt: Date.now() };
    malWiderrufBadge(meine ?? r.offen, meine !== null);
  } catch {
    // Still: ein fehlgeschlagener Zähler darf die Navigation nicht stören.
  } finally {
    widerrufLaeuft = false;
  }
}

/**
 * Die Rolle aus der Adresse des gefundenen Eintrags — und daraus, wie viele
 * Fälle auf DIESE Rolle warten.
 *
 * ⛔ `null`, wenn der Server keine Fälle mitgeliefert hat. *Ein alter Server
 * gegen einen neuen Client darf nicht dazu führen, dass die Leiste „nichts
 * offen" behauptet.*
 */
/** Der angemeldete Nutzer — die Leiste hält ihn ohnehin in `currentUser`. */
function aktuellerUser(): UserContext {
  return currentUser as UserContext;
}

function berechneMeine(faelle: WiderrufFall[] | undefined, heute: string): number | null {
  if (!Array.isArray(faelle) || !heute) return null;
  const wurzel = document.getElementById("subsidebar");
  const link = wurzel ? widerrufLink(wurzel) : null;
  const basis = link?.getAttribute("href") ?? "/widerrufe";
  const rolle = rolleAus(basis, aktuellerUser());
  return faelle.filter((f) => !!wartetAuf(f, rolle, heute)).length;
}

function malWiderrufBadge(offen: number, istMeine = false): void {
  // Die Sidebar kann inzwischen neu gerendert worden sein — frisch suchen.
  const wurzel = document.getElementById("subsidebar");
  const link = wurzel ? widerrufLink(wurzel) : null;
  if (!link) return;

  link.querySelector(".sub-item-count")?.remove();
  link.classList.remove("has-signal");
  if (offen <= 0) return;

  const span = document.createElement("span");
  span.className = "sub-item-count";
  span.textContent = String(offen);
  span.title = istMeine
    ? `${offen} ${offen === 1 ? "Vorgang wartet" : "Vorgänge warten"} auf dich`
    : `${offen} ${offen === 1 ? "Widerruf ist" : "Widerrufe sind"} noch offen`;
  link.appendChild(span);
  // Die Hervorhebung sitzt am EINTRAG, nicht an der Zahl: der Zähler sagt
  // „wie viele", die Hervorhebung sagt „hier steht was an" — zwei Aussagen.
  link.classList.add("has-signal");

  // ── Nur DIESE Gruppe aufklappen (Justin: „nicht die ganze sidebar") ──────
  //
  // 🔑 Die Gruppe wird über das DOM gefunden, nicht über eine Liste von
  // Gruppen-Schlüsseln im Code. *Eine zweite Liste derselben Zuordnung wäre
  // eine zweite Wahrheit, die beim nächsten Umbau der Navigation still falsch
  // wird.*
  //
  // 📏 **Genau das ist am 18.09. eingetreten und hat nichts gekostet.** Bis
  // dahin hing „Widerrufe" in drei Bereichen unter drei fremden Oberkategorien
  // (`closings` · `kb-aufgaben-group` · `finance-ueberblick-group`); seither
  // ist es eine eigene Gruppe je Bereich. Der Zähler fand sie ohne Änderung —
  // eine Namensliste hätte hier stillschweigend ins Leere gezeigt.
  // 🔑 *Eine Regel, die über die Struktur läuft statt über Namen, überlebt
  // den Umbau der Struktur.*
  const gruppe = link.closest(".sub-group");
  const schluessel = gruppe?.querySelector<HTMLElement>("[data-group-toggle]")?.dataset.groupToggle;
  if (!gruppe || !schluessel) return;
  gruppe.classList.add("has-signal");
  // 🔴 **Nur wenn sie zu ist — und höchstens EINMAL je Sitzung.**
  // Zwei Gründe, und der zweite ist der wichtigere:
  // ① `setActiveRoute` rendert die Sidebar neu und ruft dieses Signal dadurch
  //    wieder auf; ohne Sperre wäre das eine Endlosschleife.
  // ② Klappt der Nutzer die Gruppe danach ZU, verschwindet ihr Schlüssel aus
  //    `offeneGruppen` — und ohne `autoGeoeffnet` würde sie beim nächsten
  //    Seitenwechsel wieder aufgehen. 🔑 *Ein Hinweis, der sich gegen den
  //    Nutzer wiederholt, wird zur Bedienung, die man umgehen lernt.* Die
  //    Hervorhebung bleibt, sie ist der bleibende Teil des Signals.
  if (!autoGeoeffnet.has(schluessel) && !offeneGruppen.has(schluessel)
      && gruppe.classList.contains("is-closed")) {
    autoGeoeffnet.add(schluessel);
    offeneGruppen.add(schluessel);
    setActiveRoute(window.location.pathname);
  }
}

// =============================================================================
// Signal „offene Kündigungen" (Justin, 26.09.2026)
// =============================================================================

/**
 * Zähler am Nav-Eintrag **„Kündigungen"** — in allen drei Bereichen.
 *
 * > **Justin, 26.09.2026:** *„der sidebar eintrag brauch auch noch ein emoji
 * > und muss die zahl der offenen fälle die man noch offen hat anzeigen"*
 *
 * ═══ 🔑 DIESELBE FUNKTION WIE DIE LISTE, NICHT EINE NACHBILDUNG ════════════
 * Gerechnet wird mit `wartetAufZeile` — genau der Funktion, die auch den Reiter
 * „Bei mir" füllt. 🔑 ***Zwei Orte, die dieselbe Frage verschieden beantworten,
 * sind zwei Wahrheiten, und die Leiste verliert gegen die Liste, ohne dass es
 * jemand merkt.*** Dieselbe Begründung wie beim Widerruf-Signal.
 *
 * ⚠️ **Die Rolle kommt aus der ADRESSE des gefundenen Eintrags.** Dieselbe
 * Person zählt unter `/finance/kuendigungen` anders als unter `/kuendigungen` —
 * für die Kundenbetreuung quittiert nur die Bereichsleitung.
 * *Wer den Fall ansieht, ist keine Eigenschaft des Falls.*
 *
 * ⛔ **Kein eigener Zähl-Endpunkt.** Geholt wird `kuendigungUebersicht()`, die
 * Quelle des Registers selbst. *Ein zweiter Endpunkt für dieselbe Zahl geht
 * irgendwann auseinander, und die kleinere gewinnt.* Dafür höchstens alle fünf
 * Minuten je Sitzung, wie beim Widerruf.
 *
 * 🔴 Ein Fehler bleibt **still**: die Navigation darf an einem Zähler nicht
 * scheitern. Aber der Stand bleibt dann **ungesetzt**, damit der nächste
 * Wechsel es erneut versucht — *eine 0 wäre eine Behauptung, die wir gerade
 * nicht belegen können.*
 */
const KUENDIGUNG_ROUTEN = ["/kuendigungen", "/kb/kuendigungen", "/finance/kuendigungen"] as const;
const KUENDIGUNG_TTL_MS = 5 * 60 * 1000;
let kuendigungStand: { meine: number; geholt: number } | null = null;
let kuendigungLaeuft = false;

/** Nach einem Vorgang aufrufen — die Zahl ist dann sofort veraltet. */
export function invalidateKuendigungSignal(): void {
  kuendigungStand = null;
}

function kuendigungLink(wurzel: ParentNode): HTMLAnchorElement | null {
  for (const r of KUENDIGUNG_ROUTEN) {
    const a = wurzel.querySelector<HTMLAnchorElement>(`a[href="${r}"]`);
    if (a) return a;
  }
  return null;
}

function malKuendigungBadge(offen: number): void {
  // Die Sidebar kann inzwischen neu gerendert worden sein — frisch suchen.
  const wurzel = document.getElementById("subsidebar");
  const link = wurzel ? kuendigungLink(wurzel) : null;
  if (!link) return;

  link.querySelector(".sub-item-count")?.remove();
  link.classList.remove("has-signal");
  if (offen <= 0) return;

  const span = document.createElement("span");
  span.className = "sub-item-count";
  span.textContent = String(offen);
  span.title = `${offen} ${offen === 1 ? "Vorgang wartet" : "Vorgänge warten"} auf dich`;
  link.appendChild(span);
  link.classList.add("has-signal");

  // Die Gruppe über das DOM finden, nicht über eine Namensliste — dieselbe
  // Begründung wie beim Widerruf: *eine Regel, die über die Struktur läuft
  // statt über Namen, überlebt den Umbau der Struktur.*
  const gruppe = link.closest(".sub-group");
  const schluessel = gruppe?.querySelector<HTMLElement>("[data-group-toggle]")?.dataset.groupToggle;
  if (!gruppe || !schluessel) return;
  gruppe.classList.add("has-signal");
  if (!autoGeoeffnet.has(schluessel) && !offeneGruppen.has(schluessel)
      && gruppe.classList.contains("is-closed")) {
    autoGeoeffnet.add(schluessel);
    offeneGruppen.add(schluessel);
    setActiveRoute(window.location.pathname);
  }
}

async function zeigeKuendigungSignal(sub: HTMLElement): Promise<void> {
  const link = kuendigungLink(sub);
  if (!link) return; // Nav-Eintrag nicht sichtbar → nichts zu tun

  if (kuendigungStand && Date.now() - kuendigungStand.geholt < KUENDIGUNG_TTL_MS) {
    malKuendigungBadge(kuendigungStand.meine);
    return;
  }
  if (kuendigungLaeuft) return;

  kuendigungLaeuft = true;
  try {
    const r = await kuendigungUebersicht();
    if (!Array.isArray(r.laufend) || !r.heute) return;
    const basis = link.getAttribute("href") ?? "/kuendigungen";
    const rolle = kRolleAus(basis, currentUser as UserContext);
    const meine = r.laufend.filter((z) => !!wartetAufZeile(z, rolle, r.heute)).length;
    kuendigungStand = { meine, geholt: Date.now() };
    malKuendigungBadge(meine);
  } catch {
    // Still: ein fehlgeschlagener Zähler darf die Navigation nicht stören.
  } finally {
    kuendigungLaeuft = false;
  }
}

// =============================================================================
// Signal „Termine ohne Ergebnis" (Justin, 14.09.2026)
// =============================================================================

/**
 * Zähler am Nav-Eintrag **„Ergebnisse"** der Terminstrecke.
 *
 * > Justin, 14.09.: *„Ergebnisse in der Sidebar sollte die Anzahl noch offener
 * > Termine ohne Ergebnis anzeigen als Zeichen, dass man was nachtragen muss."*
 *
 * ═══ 🔑 WARUM GERADE HIER ══════════════════════════════════════════════════
 * Ein Termin ohne Ergebnis ist der einzige Vorgang der Strecke, der **von
 * selbst nie wieder auffällt**: er ist gebucht, er ist vorbei, und niemand
 * wartet auf ihn. 🔑 *Was stattgefunden hat, drängt nicht — es verschwindet.*
 *
 * ⚠️ **Dritte Fassung desselben Musters** (Vertretung · Widerrufe · hier).
 * Gemeinsam ist der **Badge-Teil**, den `malSignalBadge` jetzt für alle drei
 * trägt; verschieden bleiben Quelle, Route und Text — die gehören zum Vorgang,
 * nicht zur Anzeige. ⏭️ Die beiden älteren Zähler holen ihre Zahl noch selbst;
 * sie gehören beim nächsten Anfassen auf denselben Weg.
 */
const ERGEBNIS_ROUTE = "/kb/termine/ergebnisse";
const ERGEBNIS_TTL_MS = 5 * 60 * 1000;
let ergebnisStand: { offen: number; geholt: number } | null = null;
let ergebnisLaeuft = false;

/** Nach dem Eintragen eines Ergebnisses aufrufen — die Zahl ist dann veraltet. */
export function invalidateErgebnisSignal(): void {
  ergebnisStand = null;
}

async function zeigeErgebnisSignal(sub: HTMLElement): Promise<void> {
  const link = sub.querySelector<HTMLAnchorElement>(`a[href="${ERGEBNIS_ROUTE}"]`);
  if (!link) return; // Nav-Eintrag nicht sichtbar → nichts zu tun

  if (ergebnisStand && Date.now() - ergebnisStand.geholt < ERGEBNIS_TTL_MS) {
    malSignalBadge(ERGEBNIS_ROUTE, ergebnisStand.offen, ergebnisText);
    return;
  }
  if (ergebnisLaeuft) return;

  ergebnisLaeuft = true;
  try {
    const { veBestaetigungOffen } = await import("../api/ve-termine");
    const r = await veBestaetigungOffen();
    const offen = Array.isArray(r?.offen) ? r.offen.length : null;
    // 🔴 `null` heisst **nicht messbar**, nicht „null offen" — dann bleibt der
    // Zähler weg, und der nächste Wechsel versucht es erneut.
    if (typeof offen !== "number") return;
    ergebnisStand = { offen, geholt: Date.now() };
    malSignalBadge(ERGEBNIS_ROUTE, offen, ergebnisText);
  } catch {
    // Still: ein fehlgeschlagener Zähler darf die Navigation nicht stören.
  } finally {
    ergebnisLaeuft = false;
  }
}

/**
 * 🆕 05.10.2026 (Vertragsanpassungen, K3) — Zähler am Eintrag „Offen“ der Gruppe Vertragsanpassungen,
 * NUR für die Bereichsleitung: eine Zahl in der Seitenleiste heißt bei den Geschwistern „du bist dran“
 * (E19) — für jemanden, der nicht entscheiden darf, wäre sie eine falsche Aufforderung.
 * Quelle: `vertragsanpassung/zaehler` (eine COUNT-Abfrage). `darf_entscheiden` sagt der Server.
 */
const VA_OFFEN_ROUTEN = ["/vertragsanpassungen", "/kb/vertragsanpassungen", "/finance/vertragsanpassungen"] as const;
const VA_TTL_MS = 5 * 60 * 1000;
let vaStand: { offen: number; darf: boolean; geholt: number } | null = null;
let vaLaeuft = false;

/** Nach Erfassen/Entscheiden aufrufen — die Zahl ist dann veraltet. */
export function invalidateVertragsanpassungSignal(): void {
  vaStand = null;
}

async function zeigeVertragsanpassungSignal(sub: HTMLElement): Promise<void> {
  const route = VA_OFFEN_ROUTEN.find((r) => sub.querySelector(`a[href="${r}"]`));
  if (!route) return;
  const mal = (s: { offen: number; darf: boolean }) =>
    malSignalBadge(route, s.darf ? s.offen : 0, (n) => `${n} ${n === 1 ? "Vertragsanpassung wartet" : "Vertragsanpassungen warten"} auf deine Bestätigung`);
  if (vaStand && Date.now() - vaStand.geholt < VA_TTL_MS) { mal(vaStand); return; }
  if (vaLaeuft) return;
  vaLaeuft = true;
  try {
    const { vaZaehler } = await import("../api/vertragsanpassung");
    const z = await vaZaehler();
    if (typeof z?.offen !== "number") return;
    vaStand = { offen: z.offen, darf: !!z.darf_entscheiden, geholt: Date.now() };
    mal(vaStand);
  } catch {
    // Still: ein fehlgeschlagener Zähler darf die Navigation nicht stören.
  } finally {
    vaLaeuft = false;
  }
}

/**
 * 🆕 09.10.2026 (HR → Abgleich, Entwurf K1) — Zähler am Eintrag „Abgleich“: offene Befunde
 * „zu prüfen“ plus (19.10.) offene Vorschläge der Vertrags-Routine. Der Eintrag steht nur für
 * HR-Leitung/Admin in der Leiste; der Endpunkt ist genauso gesperrt. Quelle: `hr/abgleich/zaehler`.
 */
const HR_ABGLEICH_ROUTE = "/hr/abgleich";
type HrAbgleichStand = { offen: number; befunde: number; vorschlaege: number; geholt: number };
let hrAbgleichStand: HrAbgleichStand | null = null;

/** Tooltip am Eintrag — nennt beide Quellen, damit die Zahl zum Reiter führt, auf dem sie steht. */
export function hrAbgleichSignalText(s: { befunde: number; vorschlaege: number }): string {
  const teile = [
    s.befunde ? `${s.befunde} ${s.befunde === 1 ? "Befund" : "Befunde"}` : "",
    s.vorschlaege ? `${s.vorschlaege} ${s.vorschlaege === 1 ? "Vorschlag" : "Vorschläge"}` : ""
  ].filter(Boolean);
  const eins = s.befunde + s.vorschlaege === 1;
  return `${teile.join(" und ")} ${eins ? "wartet" : "warten"} auf deine Entscheidung`;
}
let hrAbgleichLaeuft = false;

/** Nach einer Entscheidung aufrufen — die Zahl ist dann veraltet. */
export function invalidateHrAbgleichSignal(): void {
  hrAbgleichStand = null;
}

async function zeigeHrAbgleichSignal(sub: HTMLElement): Promise<void> {
  if (!sub.querySelector(`a[href="${HR_ABGLEICH_ROUTE}"]`)) return;
  const mal = (s: HrAbgleichStand) => malSignalBadge(HR_ABGLEICH_ROUTE, s.offen, () => hrAbgleichSignalText(s));
  if (hrAbgleichStand && Date.now() - hrAbgleichStand.geholt < VA_TTL_MS) { mal(hrAbgleichStand); return; }
  if (hrAbgleichLaeuft) return;
  hrAbgleichLaeuft = true;
  try {
    const { hrAbgleichZaehler } = await import("../api/hr-abgleich");
    const z = await hrAbgleichZaehler();
    if (typeof z?.offen !== "number") return;
    // Ein Worker ohne die Aufteilung (Pages vor Worker deployt) liefert nur `offen` = Befunde.
    hrAbgleichStand = { offen: z.offen, befunde: Number(z.befunde ?? z.offen), vorschlaege: Number(z.vorschlaege ?? 0), geholt: Date.now() };
    mal(hrAbgleichStand);
  } catch {
    // Still: ein fehlgeschlagener Zähler darf die Navigation nicht stören.
  } finally {
    hrAbgleichLaeuft = false;
  }
}

/**
 * 🆕 02.11.2026 (SEPA-Papiermandat, Entwurf Mystique) — Zähler am Eintrag „SEPA-Papiermandate“:
 * Scans, die auf Freigabe warten. Der Eintrag steht nur für Bereichsleitung Finance + Admin
 * (`requiresBl`); der Endpunkt ist genauso gesperrt. Quelle: `finance/sepa-papier/liste`
 * (höchstens 200 Zeilen, eine D1-Abfrage). Im Probebetrieb und bei „aus“ zählt er genauso:
 * die Scans warten trotzdem.
 */
const SEPA_PAPIER_ROUTE = "/finance/sepa-papier";
let sepaStand: { offen: number; geholt: number } | null = null;
let sepaLaeuft = false;

/** Nach Freigabe/Verwerfen aufrufen — die Zahl ist dann veraltet. */
export function invalidateSepaPapierSignal(): void {
  sepaStand = null;
}

async function zeigeSepaPapierSignal(sub: HTMLElement): Promise<void> {
  if (!sub.querySelector(`a[href="${SEPA_PAPIER_ROUTE}"]`)) return;
  const mal = (n: number) => malSignalBadge(SEPA_PAPIER_ROUTE, n, (k) => `${k} ${k === 1 ? "Papiermandat wartet" : "Papiermandate warten"} auf Freigabe`);
  if (sepaStand && Date.now() - sepaStand.geholt < VA_TTL_MS) { mal(sepaStand.offen); return; }
  if (sepaLaeuft) return;
  sepaLaeuft = true;
  try {
    const { sepaListe } = await import("../api/sepa-papier");
    const r = await sepaListe();
    if (!Array.isArray(r?.eintraege)) return;
    sepaStand = { offen: r.eintraege.filter((z) => z.zustand === "wartet").length, geholt: Date.now() };
    mal(sepaStand.offen);
  } catch {
    // Still: ein fehlgeschlagener Zähler darf die Navigation nicht stören.
  } finally {
    sepaLaeuft = false;
  }
}

const ergebnisText = (n: number): string =>
  `Bei ${n} ${n === 1 ? "Termin fehlt" : "Terminen fehlt"} noch das Ergebnis`;

/**
 * Der gemeinsame Teil aller Sidebar-Signale: Zahl an den Eintrag, Hervorhebung
 * an den Eintrag, Gruppe **einmalig** aufklappen.
 *
 * 🔑 **Die Hervorhebung sitzt am EINTRAG, nicht an der Zahl:** der Zähler sagt
 * „wie viele", die Hervorhebung sagt „hier steht was an" — zwei Aussagen.
 */
function malSignalBadge(route: string, offen: number, titel: (n: number) => string): void {
  // Die Sidebar kann inzwischen neu gerendert worden sein — frisch suchen.
  const link = document.getElementById("subsidebar")
    ?.querySelector<HTMLAnchorElement>(`a[href="${route}"]`);
  if (!link) return;

  link.querySelector(".sub-item-count")?.remove();
  link.classList.remove("has-signal");
  if (offen <= 0) return;

  const span = document.createElement("span");
  span.className = "sub-item-count";
  span.textContent = String(offen);
  span.title = titel(offen);
  link.appendChild(span);
  link.classList.add("has-signal");

  const gruppe = link.closest(".sub-group");
  const schluessel = gruppe?.querySelector<HTMLElement>("[data-group-toggle]")?.dataset.groupToggle;
  if (!gruppe || !schluessel) return;
  gruppe.classList.add("has-signal");
  // 🔴 Nur wenn zu — und höchstens EINMAL je Sitzung. Begründung steht beim
  // Widerruf-Signal: sonst Endlosschleife, und ein Hinweis, der sich gegen den
  // Nutzer wiederholt, wird zur Bedienung, die man umgehen lernt.
  if (!autoGeoeffnet.has(schluessel) && !offeneGruppen.has(schluessel)
      && gruppe.classList.contains("is-closed")) {
    autoGeoeffnet.add(schluessel);
    offeneGruppen.add(schluessel);
    setActiveRoute(window.location.pathname);
  }
}

/**
 * Re-rendert Rail- + Footer-Avatar aus dem (mutierten) currentUser — von der
 * /profil-View nach Upload/Delete aufgerufen, damit das Avatar sofort ohne
 * Reload aktualisiert. currentUser ist dieselbe Objekt-Referenz wie der
 * Router-User; die View mutiert profilbild_key, hier folgt nur der DOM-Refresh.
 */
export function refreshUserChrome(): void {
  if (!currentUser) return;
  const rail = document.querySelector(".rail-user");
  if (rail) {
    const tmp = document.createElement("div");
    tmp.innerHTML = railUserHtml(currentUser).trim();
    const fresh = tmp.firstElementChild;
    if (fresh) rail.replaceWith(fresh);
  }
  const footAvatar = document.querySelector(".subsidebar-foot .ct-avatar");
  if (footAvatar) {
    const tmp = document.createElement("div");
    tmp.innerHTML = avatarHtml({
      vollname: currentUser.vollname,
      email: currentUser.email,
      farbe: currentUser.farbe,
      profilbildKey: currentUser.profilbild_key,
      personId: currentUser.person_id,
      size: 36
    }).trim();
    const fresh = tmp.firstElementChild;
    if (fresh) footAvatar.replaceWith(fresh);
  }
  void hydrateAvatars(document); // neue Token-Platzhalter (Rail + Footer) nachladen
}

// =============================================================================
// Shell HTML
// =============================================================================

function shellHtml(user: UserContext): string {
  return `
    <div id="${SHELL_ID}" class="app-shell">
      <aside class="rail" aria-label="Bereiche">
        <a href="/" data-router-link class="rail-logo has-image" data-rail-bereich="hub" title="Hub">
          <img src="/logo.png" alt="CoTrainer" />
        </a>
        <div class="rail-divider"></div>
        <nav class="rail-nav">
          ${railItemsHtml(user)}
        </nav>
        <div class="rail-divider-foot"></div>
        ${railUserHtml(user)}
      </aside>

      <aside id="subsidebar" class="subsidebar is-hidden">
      </aside>

      <button id="subsidebar-expand-btn" class="subsidebar-expand-btn${subsidebarCollapsed ? " is-visible" : ""}" type="button" aria-label="Sidebar einblenden">
        ${icon("chevron-right", 14)}
      </button>

      <main class="main">
        <div class="topbar" id="topbar">
          ${topbarHtml(user)}
        </div>
        <div class="view-wrap">
          <div id="${VIEW_CONTAINER_ID}" class="view-container"></div>
        </div>
      </main>
    </div>
  `;
}

// =============================================================================
// Rail — alle 9 Apps-Script-Bereiche, Live + Coming-Soon
// =============================================================================

function railItemsHtml(user: UserContext): string {
  // Hub ist schon als Logo oben — alle anderen Bereiche werden hier gerendert
  return allBereicheForRail()
    .filter((b) => b.slug !== "hub")
    .map((b) => railItemHtml(b, user))
    .join("");
}

function railItemHtml(b: BereichDef, user: UserContext): string {
  const isLive = isBereichLiveForUser(b, user);
  const classes = ["rail-item"];
  if (!isLive) classes.push("is-coming-soon");

  const tooltip = isLive ? b.label : `${b.label} (kommt bald)`;
  const lockOverlay = !isLive
    ? `<span class="rail-lock-overlay">${icon("lock", 10)}</span>`
    : "";

  // Coming-Soon-Bereiche bekommen kein href + kein data-router-link → nicht klickbar
  if (!isLive) {
    return `
      <div
        data-rail-bereich="${escapeAttr(b.slug)}"
        class="${classes.join(" ")}"
        title="${escapeAttr(tooltip)}"
        aria-disabled="true"
      >
        ${icon(b.iconKey, 22)}
        ${lockOverlay}
        <span class="rail-tooltip">${escapeHtml(tooltip)}</span>
      </div>
    `;
  }

  return `
    <a href="${einstiegFuerUser(b, user) ?? defaultRouteForBereich(b)}" data-router-link
       data-rail-bereich="${escapeAttr(b.slug)}"
       class="${classes.join(" ")}"
       title="${escapeAttr(tooltip)}">
      ${icon(b.iconKey, 22)}
      <span class="rail-tooltip">${escapeHtml(tooltip)}</span>
    </a>
  `;
}

function defaultRouteForBereich(b: BereichDef): string {
  const flat = flattenedViews(b);
  const firstLive = flat.find((v) => !v.comingSoon);
  if (firstLive) return firstLive.route;
  const firstAny = flat[0];
  return firstAny ? firstAny.route : "/";
}

// =============================================================================
// Sub-Sidebar
// =============================================================================

function subsidebarHtml(user: UserContext, bereich: BereichDef | null, activeViewKey: string | null, activeParentKey: string | null = null): string {
  // Hub-Fall wird in setActiveRoute() bereits als is-hidden behandelt — diese
  // Funktion wird dann nicht aufgerufen. Falls doch (z.B. initial-Render),
  // empty string returnen damit nichts gerendert wird.
  if (!bereich || bereich.slug === "hub") return "";

  if (bereich.comingSoon) {
    // §5f: subsidebar-head ist jetzt 56px fixed → "Kommt..."-Hinweis raus aus
    // Head (Höhe bleibt bündig mit Topbar). Hinweis-Text in nav-Sektion.
    return `
      <button class="subsidebar-collapse-btn" type="button" aria-label="Sidebar einklappen">
        ${icon("chevron-left", 14)}
      </button>
      <div class="subsidebar-head">
        <div class="subsidebar-head-row">
          <span class="subsidebar-badge">${icon(bereich.iconKey, 14)}</span>
          <span class="subsidebar-title">${escapeHtml(bereich.label)}</span>
        </div>
      </div>
      <div class="subsidebar-nav">
        <div style="padding:14px 16px;font-size:13px;color:var(--paper-mute);font-style:italic;">Kommt in einer späteren Phase</div>
      </div>
      ${subsidebarFooterHtml(user)}
    `;
  }

  // 2-stufige Struktur (Vertrieb): groups + Sub-Views
  // Flat-Struktur (alle anderen): direkt views
  const navHtml = bereich.groups && bereich.groups.length > 0
    ? groupedNavHtml(bereich, user, activeViewKey, activeParentKey)
    : flatNavHtml(bereich, user, activeViewKey, activeParentKey);

  return `
    <button class="subsidebar-collapse-btn" type="button" aria-label="Sidebar einklappen">
      ${icon("chevron-left", 14)}
    </button>
    <div class="subsidebar-head">
      <div class="subsidebar-head-row">
        <span class="subsidebar-badge">${icon(bereich.iconKey, 14)}</span>
        <span class="subsidebar-title">${escapeHtml(bereich.label)}</span>
      </div>
    </div>
    <nav class="subsidebar-nav">
      ${navHtml}
    </nav>
    ${subsidebarFooterHtml(user)}
  `;
}

function flatNavHtml(bereich: BereichDef, user: UserContext, activeViewKey: string | null, activeParentKey: string | null = null): string {
  const views = visibleViewsForUser(bereich, user);
  if (views.length === 0) {
    return `<div class="subsidebar-empty">Noch keine Views verfügbar</div>`;
  }
  return views.map((v) => subItemHtml(v, activeViewKey, activeParentKey)).join("");
}

function groupedNavHtml(bereich: BereichDef, user: UserContext, activeViewKey: string | null, activeParentKey: string | null = null): string {
  // isSettings-Gruppen werden wieder als regulärer Single-Link gerendert
  // (Eintrag "Einstellungen" → /vertrieb/einstellungen). Erreicht über
  // hideAsSubTab=true an allen Sub-Views + defaultRoute auf die Container-Route.
  const groups = visibleGroupsForUser(bereich, user);
  if (groups.length === 0) {
    return `<div class="subsidebar-empty">Noch keine Bereiche verfügbar</div>`;
  }
  const activeGroup = findGroupForView(bereich, activeViewKey);
  return groups.map((g) => groupHtml(g, activeGroup?.key === g.key, activeViewKey, activeParentKey)).join("");
}

function groupHtml(g: GroupDef, isActiveGroup: boolean, activeViewKey: string | null, activeParentKey: string | null = null): string {
  // Standalone: genau 1 sichtbare View, kein hideAsSubTab → Single-Link mit
  // Group-Icon (kein Header + Sub-Tab-Block).
  const displaySubViews = g.views.filter((v) => !v.hideAsSubTab);
  if (g.views.length === 1 && displaySubViews.length === 1) {
    const v = displaySubViews[0]!;
    const isCurrent = v.key === activeViewKey;
    const classes = ["sub-group-link"];
    if (isCurrent) classes.push("is-current");
    if (v.comingSoon) classes.push("is-coming-soon");
    return `
      <a href="${escapeAttr(v.route)}" data-router-link class="${classes.join(" ")}">
        <span class="sub-group-icon">${icon(g.iconKey, 14)}</span>
        <span class="sub-group-label">${escapeHtml(g.label)}</span>
        ${v.comingSoon ? `<span class="sub-group-lock">${icon("lock", 11)}</span>` : ""}
      </a>
    `;
  }

  // Multi-View: Group-Header (klickbar = Default-Route, meist hideAsSubTab-
  // Overview-View) + sichtbare Sub-Tab-Liste darunter.
  const headerRoute = defaultRouteForGroup(g);
  // Header gilt als "current" wenn aktive View die hideAsSubTab-Overview ist.
  const headerView = g.views.find((v) => v.hideAsSubTab);
  const isHeaderCurrent = headerView != null && headerView.key === activeViewKey;

  const headerClasses = ["sub-group-header"];
  if (isHeaderCurrent) headerClasses.push("is-current");
  else if (isActiveGroup) headerClasses.push("is-active-group");

  const subTabsHtml = displaySubViews
    .map((v) => subItemHtml(v, activeViewKey, activeParentKey))
    .join("");

  // ═══ AUFKLAPPBARE OBERKATEGORIEN (Justin 2026-08-14) ══════════════════════
  // 🔑 Die Gruppe, in der man GERADE STEHT, ist immer offen — sonst verlöre man
  // beim Aufräumen der Liste die Antwort auf „wo bin ich". Alles andere ist zu
  // und öffnet sich auf Klick.
  //
  // Zwei Klickziele, bewusst getrennt: der TITEL navigiert wie bisher zur
  // Übersicht der Gruppe (und klappt sie dadurch auf, weil sie danach die
  // aktive ist), der PFEIL klappt nur auf und zu. Ein Header, der beides auf
  // denselben Klick legte, könnte „ich will nur nachsehen" nicht von „bring
  // mich dorthin" unterscheiden.
  const offen = !subTabsHtml || isActiveGroup || isHeaderCurrent || offeneGruppen.has(g.key);
  const gruppeClasses = ["sub-group"];
  if (subTabsHtml) gruppeClasses.push(offen ? "is-open" : "is-closed");

  const pfeil = subTabsHtml
    ? `<button type="button" class="sub-group-toggle" data-group-toggle="${escapeAttr(g.key)}"
               aria-expanded="${offen}" aria-label="${escapeAttr(g.label)} ${offen ? "zuklappen" : "aufklappen"}">
         ${icon(offen ? "chevron-down" : "chevron-right", 12)}
       </button>`
    : "";

  return `
    <div class="${gruppeClasses.join(" ")}">
      <div class="sub-group-head-row">
        <a href="${escapeAttr(headerRoute)}" data-router-link class="${headerClasses.join(" ")}">
          <span class="sub-group-icon">${icon(g.iconKey, 14)}</span>
          <span class="sub-group-label">${escapeHtml(g.label)}</span>
        </a>
        ${pfeil}
      </div>
      ${subTabsHtml && offen ? `<div class="sub-group-views">${subTabsHtml}</div>` : ""}
    </div>
  `;
}

function subItemHtml(v: ViewDef, activeViewKey: string | null, activeParentKey: string | null = null): string {
  const classes = ["sub-item"];
  // 🔑 Aktiv ist der Eintrag selbst ODER der sichtbare Eintrag, zu dem sich ein
  // versteckter Reiter bekennt (`reiterVon`).
  if (v.key === activeViewKey || (activeParentKey && v.key === activeParentKey)) classes.push("is-current");
  if (v.comingSoon) classes.push("is-coming-soon");
  const lock = v.comingSoon ? `${icon("lock", 12)} ` : "";
  // Additiv (Finance-Subsidebar v3): per-View-Icon + per-View-Tag. Nur gerendert
  // wenn gesetzt — Views ohne iconKey/tag (HR/KB/Vertrieb) bleiben unverändert.
  const iconHtml = v.iconKey ? `<span class="sub-item-icon">${icon(v.iconKey, 14)}</span>` : "";
  const tagHtml = v.tag
    ? `<span class="sub-tag ${v.tag.kind === "neu" ? "neu" : "f"}">${escapeHtml(v.tag.label)}</span>`
    : "";
  return `
    <a href="${escapeAttr(v.route)}" data-router-link class="${classes.join(" ")}">
      ${iconHtml}${lock}${escapeHtml(v.label)}${tagHtml}
    </a>
  `;
}

/** Rail-Avatar unten in der Rail — immer sichtbar, klickbar → /profil. */
function railUserHtml(user: UserContext): string {
  return `
    <a href="/profil" data-router-link class="rail-user" title="${escapeAttr(user.vollname || user.email)} — Profil">
      ${avatarHtml({
        vollname: user.vollname,
        email: user.email,
        farbe: user.farbe,
        profilbildKey: user.profilbild_key,
        personId: user.person_id,
        size: 36
      })}
    </a>
  `;
}

function subsidebarFooterHtml(user: UserContext): string {
  const adminBadge = user.isAdmin
    ? `<span class="subsidebar-foot-admin">Admin</span>`
    : "";
  return `
    <div class="subsidebar-foot">
      <a class="subsidebar-foot-row subsidebar-foot-link" href="/profil" data-router-link title="Profil öffnen">
        <div class="subsidebar-foot-info">
          <div class="subsidebar-foot-user">${escapeHtml(user.vollname || user.email)}</div>
          <div class="subsidebar-foot-email">${escapeHtml(user.email)} · ${escapeHtml(user.person_id || "?")}</div>
        </div>
        ${adminBadge}
      </a>
    </div>
  `;
}

// =============================================================================
// Topbar — Sub-Iter 2: Pills 1:1 zu app/Index.html AppTopbar
// =============================================================================

function topbarHtml(user: UserContext): string {
  const hub = cachedBootstrap?.hub_context ?? null;
  return `
    <div class="tb-spacer"></div>
    ${renderBereichsziel(hub)}
    ${renderCallerSollPills(user, hub)}
    ${renderReminderPills(user, hub)}
    <div class="tb-user-area">
      <div id="topbar-stempel-slot"></div>
      ${user._fromCache ? `<span class="header-badge header-badge-cache" title="UserContext aus KV-Cache">cache</span>` : ""}
      <button id="header-logout-btn" type="button" class="app-logout-btn" aria-label="Logout" title="Logout">${icon("log-out", 18)}</button>
    </div>
  `;
}

// =============================================================================
// Bereichsziel-Block (1:1 zu app/Index.html Z. 508-532)
// =============================================================================

function renderBereichsziel(hub: HubContext | null): string {
  const bz = hub?.bereichsziel;
  if (!bz || !bz.gesetzt) return "";

  const fortschritt = bz.fortschritt_pct ?? 0;
  const istYear = bz.ist_year ?? 0;
  const wert = bz.wert ?? 0;
  const fillCls = bereichszielFillClass(bz.status);

  const jahr = new Date().getFullYear();
  const label = `Closings ${jahr}`;
  const valStr = `${istYear}/${wert}`;
  const pctStr = `${fortschritt}%`;

  return `
    <a class="tb-bz" href="/ziele" data-router-link
       title="Vertriebs-Bereichsziel — klick führt zur Ziele-View">
      <div class="tb-bz-row">
        <span class="tb-bz-label">${escapeHtml(label)}</span>
        <span class="tb-bz-val">${escapeHtml(valStr)}</span>
        <span class="tb-bz-pct">${escapeHtml(pctStr)}</span>
      </div>
      <div class="tb-bz-bar">
        <div class="tb-bz-fill ${fillCls}" style="width:${fortschritt}%"></div>
      </div>
    </a>
  `;
}

function bereichszielFillClass(status?: string): string {
  if (status === "verzug") return "warning";
  if (status === "kritisch" || status === "stichtag_ueberschritten") return "danger";
  return "";
}

// =============================================================================
// Soll-Pills (PG/MT für Caller — 1:1 zu app/Index.html Z. 535-571)
// =============================================================================

function renderCallerSollPills(user: UserContext, hub: HubContext | null): string {
  if (!user.aktiveFunktionen.includes("Caller")) return "";

  const k = hub?.caller_kpis;
  if (!k) {
    return (
      `<span class="tb-soll-pill" title="Caller-Werte diese Woche"><span class="lbl">PG</span><span>—</span></span>` +
      `<span class="tb-soll-pill" title="Caller-Werte diese Woche"><span class="lbl">MT</span><span>—</span></span>`
    );
  }

  const clsPg = classifyErfuellung(k.pg, k.soll_pg);
  const clsMt = classifyErfuellung(k.mt, k.soll_mt);

  return sollPillHtml("PG", k.pg, k.soll_pg, clsPg) + sollPillHtml("MT", k.mt, k.soll_mt, clsMt);
}

function sollPillHtml(label: string, ist: number, soll: number, cls: string): string {
  const valStr = soll && soll > 0 ? `${ist}/${soll}` : String(ist);
  return `
    <span class="tb-soll-pill ${cls}" title="${escapeAttr(label)} diese Woche">
      <span class="lbl">${escapeHtml(label)}</span>
      <span>${escapeHtml(valStr)}</span>
    </span>
  `;
}

function classifyErfuellung(ist: number, soll: number): string {
  if (!soll || soll === 0) return "";
  const erf = ist / soll;
  if (erf >= 1.0) return "ok";
  if (erf >= 0.7) return "warn";
  return "crit";
}

// =============================================================================
// Reminder-Pills (Pflegelücken + HubSpot — 1:1 zu app/Index.html Z. 574-619)
// =============================================================================

function renderReminderPills(user: UserContext, hub: HubContext | null): string {
  const hatVertriebsBezug =
    user.aktiveFunktionen.includes("Caller") ||
    user.aktiveFunktionen.includes("Host_MT") ||
    user.aktiveFunktionen.includes("Host_FU");
  const isAdmin = !!user.isAdmin;
  const isBL = user.berechtigungen.some(
    (b) => b.bereich.toLowerCase() === "vertrieb" && b.rolle.toLowerCase() === "bereichsleiter"
  );
  if (!hatVertriebsBezug && !isAdmin && !isBL) return "";

  // null = Bootstrap-Teilabfrage fehlgeschlagen/unbekannt (NICHT 0). Echte 0 = {count:0}.
  // Verhindert, dass ein Read-Fehler unter Sheets-Last als "0" erscheint (Justin 2026-05-30).
  const pflegCount = hub?.pfleg_pill ? hub.pfleg_pill.count : null;
  const hsCount = hub?.hubspot_tasks ? hub.hubspot_tasks.count : null;

  return reminderPillHtml("Pflegelücken", pflegCount, "/pflegeluecken") +
         reminderPillHtml("HubSpot", hsCount, null);
}

function reminderPillHtml(label: string, count: number | null, navTo: string | null): string {
  const unknown = count === null;
  const display = unknown ? "—" : String(count);
  const cls = unknown ? "count-unknown" : severityClassByCount(count);
  const canNav = !!navTo && !unknown && count > 0;
  const navAttrs = canNav ? ` href="${escapeAttr(navTo)}" data-router-link` : "";
  const tag = canNav ? "a" : "span";
  const title = unknown
    ? `${label} — aktuell nicht verfügbar (lädt)`
    : `${label} (own-only)`;
  return `
    <${tag} class="tb-rem-pill ${cls}"${navAttrs} title="${escapeAttr(title)}">
      <span>${escapeHtml(label)}</span>
      <span class="count">${escapeHtml(display)}</span>
    </${tag}>
  `;
}

// Severity-Mapping nach Count: 0=grau (count-zero), 1-5=gelb (severity-low),
// 6-10=orange (severity-med), 11+=rot (severity-high).
function severityClassByCount(count: number): string {
  if (count <= 0) return "count-zero";
  if (count <= 5) return "severity-low";
  if (count <= 10) return "severity-med";
  return "severity-high";
}

// =============================================================================
// Handlers
// =============================================================================

function attachShellHandlers(): void {
  attachSubsidebarHandlers();
  attachTopbarHandlers();
}

function attachSubsidebarHandlers(): void {
  document
    .querySelector<HTMLButtonElement>(".subsidebar-collapse-btn")
    ?.addEventListener("click", () => toggleSubsidebar());

  document
    .getElementById("subsidebar-expand-btn")
    ?.addEventListener("click", () => toggleSubsidebar());

  // Auf-/Zuklappen der Oberkategorien. Der Pfeil navigiert NICHT — deshalb hier
  // `preventDefault`/`stopPropagation`, damit der Klick nicht als Routenwechsel
  // beim umgebenden Header landet.
  document.querySelectorAll<HTMLButtonElement>("[data-group-toggle]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const key = btn.dataset.groupToggle;
      if (!key) return;
      if (offeneGruppen.has(key)) offeneGruppen.delete(key);
      else offeneGruppen.add(key);
      // Neu rendern über den regulären Weg — so bleibt „aktive Gruppe ist immer
      // offen" die EINE Stelle, an der über Offenheit entschieden wird.
      setActiveRoute(window.location.pathname);
    });
  });
}

function attachTopbarHandlers(): void {
  document.getElementById("header-logout-btn")?.addEventListener("click", () => void runLogout());
  // Topbar-Stempeluhr (Phase 8) — Live-Widget, mountet sich nach jedem Topbar-Render.
  const stempelSlot = document.getElementById("topbar-stempel-slot");
  if (stempelSlot) mountTopbarStempel(stempelSlot);
}

function toggleSubsidebar(): void {
  subsidebarCollapsed = !subsidebarCollapsed;
  const sub = document.getElementById("subsidebar");
  const expandBtn = document.getElementById("subsidebar-expand-btn");
  sub?.classList.toggle("is-collapsed", subsidebarCollapsed);
  expandBtn?.classList.toggle("is-visible", subsidebarCollapsed);
}

async function runLogout(): Promise<void> {
  const btn = document.getElementById("header-logout-btn") as HTMLButtonElement | null;
  if (btn) {
    btn.disabled = true;
  }
  await logoutBackend();
  window.location.href = "/";
}

// =============================================================================
// Helpers
// =============================================================================

// =============================================================================
// Re-exports
// =============================================================================

export { findViewForRoute };
